"use client"

import SelectImageButton from "@/(admin)/components/image/select-image-button"
import AccessDenied from "@/components/custom-access-denied"
import { BANNERS } from "@/constants/admin-paths"
import { useCheckPermission } from "@/hook/use-check-permission"
import { useBanners } from "@/hook/useBanners"
import { useGuardedForm } from "@/hook/useGuardedForm"
import { t } from "@/languages/i18n"
import { MediaData } from "@/lib/config/types"
import { addBanner, fetchBannerOptions, updateBanner } from "@/services/banner"
import { BannerData } from "@/types/types"
import { createPath } from "@/utils/create-path"
import { normalizeFormData } from "@/utils/normalize-form-data"
import { FormSection, FormShell, FormSkeleton, StickyActionBar } from "@/(admin)/components/form"
import { SaveOutlined } from "@ant-design/icons"
import { Button, Form, Input, InputNumber, Select } from "antd"
import { useRouter, useSearchParams } from "next/navigation"
import { useCallback, useEffect, useState } from "react"

const { TextArea } = Input

interface SelectOption {
  value: string | number
  label: string
}

const BannerUpsertForm: React.FC = () => {
  const [form] = Form.useForm()
  const router = useRouter()
  const searchParams = useSearchParams()
  const id = searchParams.get("id")
  const editMode = Boolean(id)
  const { hasPermission: checkPermission } = useCheckPermission()
  const [loading, setLoading] = useState(false)
  const [bannerData, setBannerData] = useState<BannerData | null>(null)
  const [isFormReady, setIsFormReady] = useState(false);
  const requiredPermission = editMode ? "update" : "create"

  if (!checkPermission("banner", requiredPermission)) {
    return <AccessDenied />
  }
  const [selectedBannerImage, setSelectedBannerImage] = useState<MediaData[]>([])
  const [selectedMobileBannerImage, setSelectedMobileBannerImage] = useState<MediaData[]>([])
  const [selectOptions, setSelectOptions] = useState<{
    pageOptions: SelectOption[]
    positionOptions: SelectOption[]
    websiteOptions: SelectOption[]
    targetOptions: SelectOption[]
  }>({
    pageOptions: [],
    positionOptions: [],
    websiteOptions: [],
    targetOptions: [],
  })

  const {
    actions: { getBannerInformation },
    bannerItemData,
  } = useBanners()

  const getNormalizedSnapshot = useCallback(() => {
    return {
      ...normalizeFormData(form.getFieldsValue(true)),
      selectedBannerImage: selectedBannerImage.length > 0 ? selectedBannerImage[0].id : null,
      selectedMobileBannerImage: selectedMobileBannerImage.length > 0 ? selectedMobileBannerImage[0].id : null,
    };
  }, [form, selectedBannerImage, selectedMobileBannerImage])

  const { checkChange, commitSnapshot, message, handleCancel: guardCancel } = useGuardedForm({
    form,
    getSnapshot: getNormalizedSnapshot
  });


  useEffect(() => {
    checkChange();
  }, [selectedBannerImage, selectedMobileBannerImage, checkChange]);

  useEffect(() => {
    if (!editMode) {
      setIsFormReady(true);
    }
  }, [editMode]);

  useEffect(() => {
    const initializeData = async () => {
      try {
        const bannerOptions = await fetchBannerOptions()
        setSelectOptions(bannerOptions)
      } catch (error) {
        message.error(t("Không thể tải tùy chọn biểu mẫu. Vui lòng thử lại."))
      }

      if (editMode && id) {
        try {
          setLoading(true)
          await getBannerInformation(Number(id))
        } catch (error) {
          message.error(t("Không thể tải dữ liệu banner. Vui lòng thử lại."))
        } finally {
          setLoading(false)
        }
      }
    }

    initializeData()
  }, [id])

  useEffect(() => {
    if (!bannerItemData || !editMode) return

    setBannerData(bannerItemData)

    const { attributes } = bannerItemData
    form.setFieldsValue({
      name: attributes.name,
      description: attributes.description,
      position: attributes.position,
      page: attributes.page,
      url: attributes.url,
      order: attributes.order,
      target: attributes.target,
      website: attributes.website?.data?.id,
      clickUrl: attributes.common?.clickUrl,
      impressionHtml: attributes.common?.impressionHtml
        ? JSON.stringify(attributes.common.impressionHtml, null, 2)
        : undefined,
      vendorScriptSrc: attributes.common?.vendorScriptSrc,
      vendorNoscriptHtml: attributes.common?.vendorNoscriptHtml,
    })

    if (attributes.banner?.data) {
      setSelectedBannerImage([attributes.banner.data])
    }
    if (attributes.mobileBanner?.data) {
      setSelectedMobileBannerImage([attributes.mobileBanner.data])
    }
    setIsFormReady(true);
  }, [bannerItemData])

  useEffect(() => {
    if (selectedBannerImage.length > 0) {
      form.setFieldsValue({ banner: selectedBannerImage[0].id })
    } else {
      form.setFieldsValue({ banner: undefined })
    }
  }, [selectedBannerImage, form])

  useEffect(() => {
    if (isFormReady) {
      const animationId = requestAnimationFrame(() => {
        commitSnapshot();
      });

      return () => cancelAnimationFrame(animationId);
    }
  }, [isFormReady, commitSnapshot]);

  const handleSubmit = useCallback(
    async (formValues: any) => {
      try {
        setLoading(true)

        let impressionHtmlParsed = undefined
        if (formValues.impressionHtml) {
          try {
            impressionHtmlParsed = JSON.parse(formValues.impressionHtml)
          } catch (error) {
            console.warn("impressionHtml is not valid JSON:", error)
          }
        }

        const commonData: any = {}
        if (formValues.clickUrl) commonData.clickUrl = formValues.clickUrl
        if (impressionHtmlParsed) commonData.impressionHtml = impressionHtmlParsed
        if (formValues.vendorScriptSrc) commonData.vendorScriptSrc = formValues.vendorScriptSrc
        if (formValues.vendorNoscriptHtml) commonData.vendorNoscriptHtml = formValues.vendorNoscriptHtml

        const bannerPayload = {
          data: {
            name: formValues.name,
            description: formValues.description,
            position: formValues.position,
            page: formValues.page,
            url: formValues.url,
            order: formValues.order,
            target: formValues.target,
            website: formValues.website,
            banner: selectedBannerImage.length > 0 ? selectedBannerImage[0].id : 0,
            ...(selectedMobileBannerImage.length > 0 && { mobileBanner: selectedMobileBannerImage[0].id }),
            ...(Object.keys(commonData).length > 0 && { common: commonData }),
          },
        }

        if (editMode && id) {
          await updateBanner(Number(id), bannerPayload)
          commitSnapshot();
          message.success(t("Banner đã được cập nhật thành công"))
        } else {
          await addBanner(bannerPayload)
          commitSnapshot();
          message.success(t("Banner đã được tạo thành công"))
          router.push(createPath(BANNERS.LIST.PATH, searchParams))
        }


      } catch (error) {
        message.error(t("Không thể lưu banner. Vui lòng thử lại."))
      } finally {
        setLoading(false)
      }
    },
    [id, selectedBannerImage, selectedMobileBannerImage],
  )

  if (editMode && !isFormReady) {
    return <FormSkeleton mainSections={3} sidebarCards={1} />
  }

  const crumbs = [
    { title: t("Banner"), href: createPath(BANNERS.LIST.PATH, searchParams) },
    { title: editMode ? t("Chỉnh sửa") : t("Tạo mới") },
  ]

  const main = (
    <>
      <FormSection title={t("Thông tin cơ bản")} collapsible={false}>
        <Form.Item
          name="name"
          label={<span className="text-base font-medium">{t("Tên Banner")}</span>}
          rules={[{ required: true, message: t("Vui lòng nhập tên banner") }]}
        >
          <Input placeholder={t("Nhập tên banner")} />
        </Form.Item>

        <Form.Item name="description" label={<span className="text-base font-medium">{t("Mô tả")}</span>}>
          <TextArea rows={4} placeholder={t("Nhập mô tả banner")} />
        </Form.Item>

        <div className="grid grid-cols-3 gap-4">
          <Form.Item
            name="position"
            label={<span className="text-base font-medium">{t("Vị trí")}</span>}
            rules={[{ required: true, message: t("Vui lòng chọn vị trí") }]}
          >
            <Select
              placeholder={t("Chọn vị trí")}
              options={selectOptions.positionOptions.map((option, index) => ({
                ...option,
                key: `position-${option.value}-${index}`,
              }))}
              showSearch
              filterOption={(input, option) => (option?.label ?? "").toLowerCase().includes(input.toLowerCase())}
            />
          </Form.Item>

          <Form.Item
            name="page"
            label={<span className="text-base font-medium">{t("Display On")}</span>}
            rules={[{ required: true, message: t("Vui lòng chọn trang") }]}
          >
            <Select
              placeholder={t("Chọn trang")}
              options={selectOptions.pageOptions.map((option, index) => ({
                ...option,
                key: `page-${option.value}-${index}`,
              }))}
              showSearch
              filterOption={(input, option) => (option?.label ?? "").toLowerCase().includes(input.toLowerCase())}
            />
          </Form.Item>

          <Form.Item
            name="order"
            label={<span className="text-base font-medium">{t("Thứ tự")}</span>}
            rules={[{ required: true, message: t("Vui lòng nhập thứ tự") }]}
          >
            <InputNumber min={1} placeholder={t("Nhập thứ tự")} className="w-full" />
          </Form.Item>
        </div>

        <Form.Item
          name="url"
          label={<span className="text-base font-medium">URL</span>}
          rules={[{ required: true, message: t("Vui lòng chọn URL") }]}
        >
          <Input placeholder={t("Nhập URL banner")} />
        </Form.Item>

        <div className="grid grid-cols-2 gap-4">
          <Form.Item name="website" label={<span className="text-base font-medium">{t("Website")}</span>}>
            <Select
              options={selectOptions.websiteOptions.map((option, index) => ({
                ...option,
                key: `website-${option.value}-${index}`,
              }))}
              placeholder={t("Chọn website")}
            />
          </Form.Item>

          <Form.Item name="target" label={<span className="text-base font-medium">{t("Mục tiêu")}</span>} className="!mb-0">
            <Select
              options={selectOptions.targetOptions.map((option, index) => ({
                ...option,
                key: `target-${option.value}-${index}`,
              }))}
              placeholder={t("Chọn mục tiêu")}
            />
          </Form.Item>
        </div>
      </FormSection>

      <FormSection title={t("Cấu hình Quảng cáo")} defaultOpen={false}>
        <Form.Item name="clickUrl" label={<span className="text-base font-medium">Click URL</span>}>
          <TextArea rows={3} placeholder={t("Nhập URL click tracking")} className="font-mono text-sm" />
        </Form.Item>

        <Form.Item name="impressionHtml" label={<span className="text-base font-medium">Impression HTML</span>}>
          <TextArea rows={3} placeholder={t("Nhập HTML cho impression tracking")} className="font-mono text-sm" />
        </Form.Item>

        <Form.Item name="vendorScriptSrc" label={<span className="text-base font-medium">Vendor Script Src</span>}>
          <TextArea rows={3} placeholder={t("Nhập URL script của vendor")} className="font-mono text-sm" />
        </Form.Item>

        <Form.Item
          name="vendorNoscriptHtml"
          label={<span className="text-base font-medium">Vendor Noscript HTML</span>}
          className="!mb-0"
        >
          <TextArea rows={3} placeholder={t("Nhập HTML noscript của vendor")} className="font-mono text-sm" />
        </Form.Item>
      </FormSection>
    </>
  )

  const sidebar = (
    <FormSection title={t("Ảnh Banner")} collapsible={false} compact>
      <Form.Item
        name="banner"
        label={<span className="text-sm font-medium">Banner (Desktop)</span>}
        rules={[{ required: true, message: t("Vui lòng chọn banner desktop") }]}
      >
        <SelectImageButton
          selectedMedia={selectedBannerImage}
          setSelectedMedia={setSelectedBannerImage}
          selectMode="single"
        />
      </Form.Item>

      <Form.Item name="mobileBanner" label={<span className="text-sm font-medium">Banner (Mobile)</span>} className="!mb-0">
        <SelectImageButton
          selectedMedia={selectedMobileBannerImage}
          setSelectedMedia={setSelectedMobileBannerImage}
          selectMode="single"
        />
      </Form.Item>
    </FormSection>
  )

  const actionBar = (
    <StickyActionBar
      right={
        <>
          <Button onClick={() => guardCancel(createPath(BANNERS.LIST.PATH, searchParams))}>{t("Hủy")}</Button>
          <Button type="primary" icon={<SaveOutlined />} htmlType="submit" loading={loading}>
            {editMode ? t("Cập nhật Banner") : t("Tạo Banner")}
          </Button>
        </>
      }
    />
  )

  return (
    <Form
      form={form}
      layout="vertical"
      onFinish={handleSubmit}
      initialValues={{
        target: "_blank",
        order: 1,
      }}
      onValuesChange={checkChange}
    >
      <FormShell
        title={editMode ? t("Chỉnh sửa Banner") : t("Thêm Banner mới")}
        subtitle={editMode && bannerData?.attributes?.name ? bannerData.attributes.name : undefined}
        crumbs={crumbs}
        main={main}
        sidebar={sidebar}
        actionBar={actionBar}
      />
    </Form>
  )
}

export default BannerUpsertForm
